import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { RouterSelectors } from '@next/core-lib/router-store';
import { UserActions } from '@next/core-lib/user';
import { ofType, createEffect, Actions } from '@ngrx/effects';
import { ROUTER_NAVIGATED, RouterNavigatedAction } from '@ngrx/router-store';
import { Store, select } from '@ngrx/store';

import { throwError, of, combineLatest, EMPTY, forkJoin } from 'rxjs';
import {
  switchMap,
  catchError,
  concatMap,
  startWith,
  withLatestFrom,
  take,
  map,
  finalize,
  filter,
} from 'rxjs/operators';

import * as actions from './actions';
import {
  selectShipToExternalId,
  selectSystem,
  selectUnselectedApplicationGroups,
} from './selectors';
import { AppState, SingleApplicationDatastream } from '../shared/models';
import { ApiService } from '../shared/services';

@Injectable()
export class Effects {
  protected store = inject<Store<AppState>>(Store);
  protected actions$ = inject(Actions);
  protected api = inject(ApiService);
  private readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);

  initCore$ = createEffect(() =>
    forkJoin(
      this.actions$.pipe(ofType(UserActions.userInitialized), take(1)),
      this.actions$.pipe(
        ofType(ROUTER_NAVIGATED),
        filter(({ payload }: RouterNavigatedAction) => payload.event.url.indexOf('/auth') === -1),
        take(1),
      ),
    ).pipe(
      withLatestFrom(this.store.pipe(select(RouterSelectors.getQueryParams))),
      map(([_, params]) => {
        const hasParams = params.EntityExternalId && params.System;
        if (!hasParams) {
          return CoreActions.fatalError({
            message: this.locale.translate('required_parameters_missing'),
          });
        } else {
          return actions.init({
            entityExternalId: params.EntityExternalId,
            system: params.System,
          });
        }
      }),
    ),
  );

  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.init),
      withLatestFrom(
        this.store.pipe(select(selectShipToExternalId)),
        this.store.pipe(select(selectSystem)),
      ),
      switchMap(([_, shipToExternalId, system]) =>
        combineLatest([
          this.api.getApplicationGroups(),
          this.api.getDatastreams(),
          this.api.getApplications(),
          this.api.getApplicationDatastreams(shipToExternalId, system).pipe(
            catchError((error: HttpErrorResponse) => {
              if (error.status === 404) {
                return throwError(new Error(this.locale.translate('customer_not_found')));
              }
            }),
          ),
        ]).pipe(
          concatMap(([applicationGroups, datastreams, applications, applicationDatastreams]) => [
            actions.setApplications({ applications }),
            actions.setApplicationGroups({ applicationGroups }),
            actions.setDatastreams({ datastreams }),
            actions.setApplicationDatastreams({ applicationDatastreams }),
            actions.initialized(),
          ]),
          catchError((error: Error | HttpErrorResponse) => {
            if (error instanceof HttpErrorResponse && error.status === 404) {
              return of(
                CoreActions.fatalError({
                  message: this.locale.translate('customer_not_found'),
                  code: 404,
                }),
              );
            } else {
              return of(CoreActions.fatalError({ message: error.message }));
            }
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  setGroupsDatastreams = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setGroupsDatastreams),
      concatMap(({ groupIds }) =>
        groupIds.map((groupId) => actions.setGroupDatastreams({ groupId })),
      ),
    ),
  );

  selectApplicationGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.selectApplicationGroups),
      withLatestFrom(this.store.pipe(select(selectUnselectedApplicationGroups))),
      switchMap(([_, unselectedApplicationGroups]) =>
        this.dialog.multiselect({
          options: unselectedApplicationGroups.map((applicationGroup) => ({
            value: applicationGroup.id,
            name: applicationGroup.displayName,
          })),
          title: this.locale.translate('add_applicationgroup'),
          selected: [],
          emptyText: '',
        }),
      ),
      concatMap(({ status, result }) =>
        status === DIALOG_RETURN_TYPES.confirm
          ? of(actions.setGroupsDatastreams({ groupIds: result }))
          : EMPTY,
      ),
    ),
  );

  updateApplicationDatastream$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.updateApplicationDatastream),
      switchMap(({ applicationKey, oldDataStreamKeys, dataStreamKeys }) => {
        const removedKeys = oldDataStreamKeys.filter(
          (oldKey) => dataStreamKeys.indexOf(oldKey) === -1,
        );
        const addedKeys = dataStreamKeys.filter(
          (newKey) => oldDataStreamKeys.indexOf(newKey) === -1,
        );
        if (removedKeys.length) {
          return of(
            actions.deleteApplicationDatastream({
              applicationKey,
              dataStreamKeys: removedKeys,
              oldDataStreamKeys,
            }),
          );
        } else if (addedKeys.length) {
          return of(
            actions.setApplicationDatastream({
              applicationKey,
              dataStreamKeys: addedKeys,
              oldDataStreamKeys,
            }),
          );
        }
      }),
    ),
  );

  setApplicationDatastream$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setApplicationDatastream),
      withLatestFrom(
        this.store.pipe(select(selectShipToExternalId)),
        this.store.pipe(select(selectSystem)),
      ),
      switchMap(([action, shipToExternalId, system]) =>
        combineLatest(
          action.dataStreamKeys.map((dataStreamKey) =>
            this.api.setApplicationDatastream(
              action.applicationKey,
              dataStreamKey,
              shipToExternalId,
              system,
            ),
          ),
        ).pipe(
          concatMap((_) => [
            NotificationActions.successSnackbar({
              message: this.locale.translate('saved_datastream_success'),
            }),
            CoreActions.setLoading({ loading: false }),
          ]),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  deleteApplicationDatastream$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteApplicationDatastream),
      withLatestFrom(
        this.store.pipe(select(selectShipToExternalId)),
        this.store.pipe(select(selectSystem)),
      ),
      switchMap(([action, shipToExternalId, system]) =>
        combineLatest(
          action.dataStreamKeys.map((dataStreamKey) =>
            this.api.deleteApplicationDatastream(
              action.applicationKey,
              dataStreamKey,
              shipToExternalId,
              system,
            ),
          ),
        ).pipe(
          concatMap((_) => [
            NotificationActions.successSnackbar({
              message: this.locale.translate('removed_datastream_success'),
            }),
            CoreActions.setLoading({ loading: false }),
          ]),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  deleteGroupDatastreams$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteGroupDatastreams),
      map(({ group }) =>
        group.applications.reduce(
          (acc, curr) => [
            ...acc,
            ...curr.dataStreamKeys.map((dataStreamKey) => ({
              dataStreamKey,
              applicationKey: curr.key,
            })),
          ],
          [] as SingleApplicationDatastream[],
        ),
      ),
      switchMap((applicationDatastreams) =>
        applicationDatastreams.length > 0
          ? combineLatest([
              this.store.pipe(select(selectShipToExternalId)),
              this.store.pipe(select(selectSystem)),
            ]).pipe(
              take(1),
              switchMap(([shipToExternalId, system]) =>
                combineLatest(
                  applicationDatastreams.map((applicationDatastream) =>
                    this.api.deleteApplicationDatastream(
                      applicationDatastream.applicationKey,
                      applicationDatastream.dataStreamKey,
                      shipToExternalId,
                      system,
                    ),
                  ),
                ).pipe(
                  concatMap((_) => [
                    NotificationActions.successSnackbar({
                      message: this.locale.translate('removed_datastream_success'),
                    }),
                    CoreActions.setLoading({ loading: false }),
                  ]),
                  startWith(CoreActions.setLoading({ loading: true })),
                ),
              ),
            )
          : EMPTY,
      ),
    ),
  );
}
